import { newLoadFactorDisclosure } from './newLoadFactorDisclosure';
import { removedLoadDisclosure } from './removedLoadDisclosure';
import { ReportData, ReportTemplateData } from '../types/report';
import { formatCompanyAddress } from './preparerDefaults';
import { buildMergeDisclosure } from './amiMerge';
import { exclusionDisclosure } from './peakExclusions';
import { existingLoads, enteredProposedLoads } from './calc/loadRoles';
import { getEditionPack } from './calc/editions';
import { citationsFor } from './calc/citations';
import { qualificationStatement } from './calc/qualification';
import {
  basisReason,
  connectedLoadVA,
  inventoryCheck,
  MULTI_METHOD_REPORT_LINE,
} from './calc/comparison';
import { buildDemandSeries, formatLongDate, type DemandPoint } from './demandSeries';
import { layoutDemandYear, layoutToSvgString, PRINT_PALETTE } from './demandChartLayout';

/**
 * The daily-peak figure for the report: the same geometry the Existing Load
 * tab draws, set in ink on white with the report's serif, plus its caption.
 * The report only ever renders unlocked, so the scale and the peak's value
 * are always printed. Empty when the project has no interval series (a
 * manually entered peak has nothing to draw).
 */
function buildDemandFigure(
  amiData: DemandPoint[] | undefined,
  intervalMinutes: number | null
): { demand_chart_svg: string; demand_chart_caption: string } {
  const series = amiData && amiData.length > 0 ? buildDemandSeries(amiData, intervalMinutes) : null;
  if (!series) return { demand_chart_svg: '', demand_chart_caption: '' };

  const layout = layoutDemandYear(series, { width: 700, height: 260, unlocked: true, palette: PRINT_PALETTE });
  const first = formatLongDate(series.days[0].date);
  const last = formatLongDate(series.days[series.days.length - 1].date);
  const gaps =
    series.gaps.length === 0
      ? 'No gaps in the record'
      : series.gaps.length === 1
        ? `One ${series.gaps[0].minutes}-minute gap on ${formatLongDate(series.gaps[0].date)}`
        : `${series.gaps.length} gaps in the record, the longest ${Math.max(...series.gaps.map(g => g.minutes))} minutes`;
  // The count is the project's, the same number the tab and the AMI Measurement
  // block state, not the whole-day subset the chart draws.
  const caption =
    `Figure 1. Highest ${series.intervalMinutes}-minute demand recorded each day, ${first} to ${last} ` +
    `(${amiData!.length.toLocaleString()} readings). Maximum demand ${series.peak.kw.toFixed(2)} kW on ` +
    `${formatLongDate(series.peak.date)} at ${series.peak.timeLabel} (${series.peak.season}). ${gaps}.`;
  return {
    demand_chart_svg: layoutToSvgString(layout, {
      fontFamily: "'Times New Roman', Times, serif",
      ariaLabel: `Daily peak demand, ${first} to ${last}. Maximum ${series.peak.kw.toFixed(2)} kW on ${formatLongDate(series.peak.date)} at ${series.peak.timeLabel}.`,
    }),
    demand_chart_caption: caption,
  };
}

/**
 * The Methods Computed block (program spec §6). Built only when BOTH methods
 * genuinely ran; otherwise every field is absent and both templates render
 * byte-identically to their single-method output. Rows include failing
 * methods on purpose — suppressing one is what would turn a legitimate
 * comparison into cherry picking.
 */
function buildMethodsBlock(reportData: ReportData): Partial<ReportTemplateData> {
  const { results, traditionalResults: tr } = reportData;
  // Opt-in only: the default report carries the basis method alone.
  if (!reportData.includeMethodsComparison || !reportData.measuredRan || !tr) return {};
  const basis: 'measured' | 'traditional' =
    reportData.method === 'traditional' ? 'traditional' : 'measured';

  const connectedKVA = connectedLoadVA(tr.lineItems) / 1000;
  const inv = inventoryCheck(results.maxRecordedDemandKVA, connectedKVA);

  return {
    methods_summary_rows: [
      {
        label: `Measured demand — NEC ${citationsFor(reportData.project).measured} Condition (1)`,
        demand_kva: results.adjustedDemandKVA.toFixed(2),
        margin_kva: results.availableMarginKVA.toFixed(2),
        result: results.adequacyResult,
        is_basis: basis === 'measured',
      },
      {
        label: `Traditional calculation — NEC ${tr.sectionCitation}`,
        demand_kva: tr.totalDemandKVA.toFixed(2),
        margin_kva: tr.availableMarginKVA.toFixed(2),
        result: tr.adequacyResult,
        is_basis: basis === 'traditional',
      },
    ],
    basis_reason: basisReason(basis, tr.sectionCitation, citationsFor(reportData.project).measured),
    multi_method_line: MULTI_METHOD_REPORT_LINE,
    inventory_check_line: inv
      ? `Inventory cross-check: metered peak demand (${results.maxRecordedDemandKVA.toFixed(2)} kVA) equals ${inv.percent}% of the entered connected load (${connectedKVA.toFixed(2)} kVA).`
      : undefined,
  };
}

/**
 * The provenance line printed under the calculation summary. Built here rather
 * than in the templates so the screen report and the PDF cannot drift.
 *
 * Each sentence is dropped whole when the fields behind it are missing, so a
 * sparse or draft project still produces a readable line instead of a sentence
 * full of "N/A".
 */
function buildCalculationBasis(params: {
  provider: string;
  period: string;
  intervalMinutes: string;
  peakKW: string;
  peakTimestamp: string;
  peakSeason: string;
  methodologyVersion: string;
  citation: string;
}): string {
  const sentences: string[] = [];

  const sourceParts = [
    params.provider ? `${params.provider} AMI interval data` : 'Utility AMI interval data',
    params.period,
    params.intervalMinutes ? `${params.intervalMinutes}-minute intervals` : '',
  ].filter(Boolean);
  sentences.push(`Basis: ${sourceParts.join(', ')}.`);

  if (params.peakKW) {
    const when = [
      params.peakTimestamp && params.peakTimestamp !== 'N/A' ? ` on ${params.peakTimestamp}` : '',
      params.peakSeason ? ` (${params.peakSeason})` : '',
    ].join('');
    sentences.push(`Measured peak demand ${params.peakKW} kW${when}.`);
  }

  sentences.push(
    `Method: NEC ${params.citation} Condition (1)${
      params.methodologyVersion ? ` — ${params.methodologyVersion}` : ''
    }. Supporting detail follows.`,
  );

  return sentences.join(' ');
}

/**
 * Equipment taken out with this work, for either report. Built from the
 * measured result's removed list (always computed, whatever the basis) with
 * the row details looked up from the full equipment list.
 */
function buildRemovedFields(
  reportData: ReportData,
  method: 'measured' | 'traditional',
  cite: string
): Pick<ReportTemplateData, 'removed_list' | 'removed_credit_applied' | 'removed_credit_kva' | 'removed_disclosure'> {
  const { project, equipment, results } = reportData;
  const removed = results.removedCalculations ?? [];
  return {
    removed_list: removed.map((calc) => {
      const eq = equipment.find((e) => e.id === calc.equipmentId);
      return {
        equipment_name: calc.equipmentName,
        equipment_type: eq?.equipment_type || 'Other',
        source: eq?.source || 'Nameplate',
        quantity: eq?.quantity?.toString() || '1',
        voltage: eq?.voltage?.toString() || '',
        amps: eq?.amps?.toString() || 'N/A',
        kw: eq?.kw?.toString() || 'N/A',
        kva: calc.kva.toFixed(2),
        is_concurrent: calc.isConcurrent ? 'Yes' : 'No',
        credit_kva: (method === 'measured' ? calc.creditKVA : 0).toFixed(2),
        concurrency_reason: method === 'measured' ? eq?.concurrency_reason || '' : '',
      };
    }),
    removed_credit_applied: method === 'measured' && results.removedLoadCreditApplied && removed.length > 0,
    removed_credit_kva: results.totalRemovedCreditKVA.toFixed(2),
    removed_disclosure: removedLoadDisclosure(project, results, method, cite),
  };
}

/** Readings are fixed-UTC wall clock, so the meter's own time is the UTC reading. */
function reportWhen(iso: string): string {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' });
}

/** The Data Quality Exclusions section: raw peak, each window, the peak used. */
function buildExclusionSection(
  project: ReportData['project'],
  usedKw: number,
  usedTimestamp: string | null
): ReportTemplateData['data_quality_exclusions'] {
  const d = exclusionDisclosure(project, { peakKw: usedKw, peakTimestamp: usedTimestamp });
  if (!d) return null;
  const hours = (h: number) => `${Math.round(h * 10) / 10} hours`;
  return {
    raw_peak: `${d.rawPeakKw.toFixed(2)} kW at ${reportWhen(d.rawPeakTimestamp)}`,
    used_peak: `${d.usedPeakKw.toFixed(2)} kW at ${reportWhen(d.usedPeakTimestamp)}`,
    total_hours: hours(d.totalHours),
    statement: d.statement,
    windows: d.windows.map(w => ({
      window: `${reportWhen(w.start)} to ${reportWhen(w.end)} (${hours(w.hours)})`,
      check: w.check,
      reason: w.reason,
      note: w.note,
      by: w.by,
      at: reportWhen(w.at),
    })),
  };
}

export function formatReportData(reportData: ReportData): ReportTemplateData {
  if (reportData.method === 'traditional') {
    return formatTraditionalReportData(reportData);
  }
  const { project, equipment, results } = reportData;
  const isDraft = reportData.isDraft ?? false;

  const customerCityStateZip = [
    project.customer_city,
    project.customer_state,
    project.customer_zip,
  ]
    .filter(Boolean)
    .join(', ');

  const reportDate =
    project.report_date || new Date().toLocaleDateString('en-US');

  const equipmentList = results.equipmentCalculations.map((calc) => {
    const equipmentItem = equipment.find((eq) => eq.id === calc.equipmentId);
    return {
      equipment_name: calc.equipmentName,
      equipment_type: equipmentItem?.equipment_type || 'Other',
      source: equipmentItem?.source || 'Nameplate',
      quantity: equipmentItem?.quantity?.toString() || '1',
      voltage: equipmentItem?.voltage?.toString() || '',
      amps: equipmentItem?.amps?.toString() || 'N/A',
      kw: equipmentItem?.kw?.toString() || 'N/A',
      kva: calc.kva.toFixed(2),
      is_concurrent: calc.isConcurrent ? 'Yes' : 'No',
      concurrency_reason: equipmentItem?.concurrency_reason || '',
      demand_factor: results.newLoadFactorsApplied ? `${Math.round(calc.demandFactor * 100)}%` : '',
      counted_kva: calc.demandKVA.toFixed(2),
    };
  });

  // Every section number on the measured report resolves here. A stored
  // methodology_version from before the edition packs names the section it was
  // written against; the report states the edition it is being read under, so
  // the citation wins over the stored string.
  const cites = citationsFor(project);
  const methodologyVersion = `NEC ${cites.measured} v1.0`;

  const adequacyStatusText =
    results.adequacyResult === 'PASS'
      ? `The existing service has adequate capacity for the proposed new loads under NEC ${cites.measured}.`
      : `The existing service does NOT have adequate capacity for the proposed new loads under NEC ${cites.measured}. Service upgrade required.`;

  const peakTimestamp = results.peakTimestamp
    ? new Date(results.peakTimestamp).toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'UTC',
      })
    : 'N/A';

  return {
    is_draft: isDraft,

    customer_name: project.customer_name || '',
    customer_address: project.customer_address || '',
    customer_city_state_zip: customerCityStateZip,

    prepared_by: project.prepared_by || '',
    preparer_title: project.preparer_title || '',
    preparer_license: project.preparer_license || '',
    company_name: project.company_name || '',
    company_address: formatCompanyAddress(project),
    firm_logo_src: reportData.firmLogoSrc || '',

    ahj_jurisdiction: project.ahj_jurisdiction || '',
    permit_number: project.permit_number || '',
    report_date: reportDate,
    methodology_version: methodologyVersion,

    service_voltage_text: project.service_voltage_text || '',
    service_rating_amps: project.service_rating_amps?.toString() || '',

    ami_data_provider: project.ami_data_provider || '',
    ami_measurement_period: project.ami_measurement_period || '',
    ami_occupancy_status: project.ami_occupancy_status || '',
    ami_interval_minutes: project.ami_interval_minutes?.toString() || '',
    ami_data_quality_notes: project.ami_data_quality_notes || '',
    ami_site_notes: project.ami_site_notes || '',
    ami_merge_disclosure: buildMergeDisclosure(
      project.ami_merge_conflicts_resolved,
      project.ami_merge_strategy,
    ),
    ...buildDemandFigure(reportData.amiData, project.ami_interval_minutes ?? null),
    data_quality_exclusions: buildExclusionSection(project, results.maxRecordedDemandKW, results.peakTimestamp ?? null),

    max_recorded_demand_kw: results.maxRecordedDemandKW.toFixed(2),
    max_recorded_demand_kva: results.maxRecordedDemandKVA.toFixed(2),
    peak_timestamp: peakTimestamp,
    peak_season: results.peakSeason,

    service_capacity_kva: results.serviceCapacityKVA.toFixed(2),
    utilization_percent: results.utilizationPercent.toFixed(1),

    existing_demand_kva: results.existingDemandKVA.toFixed(2),
    existing_demand_125_kva: results.existingDemand125KVA.toFixed(2),
    // What the new load adds to the adjusted demand — the factored figure
    // under the 2026 position, the connected figure everywhere else.
    total_concurrent_new_load_kva:
      results.totalConcurrentNewLoadDemandKVA.toFixed(2),
    total_concurrent_connected_kva: results.totalConcurrentNewLoadKVA.toFixed(2),
    total_all_new_load_kva: results.totalAllNewLoadKVA.toFixed(2),
    new_load_factors_applied: results.newLoadFactorsApplied,
    new_load_factor_disclosure: newLoadFactorDisclosure(project, results, cites.measured),
    ...buildRemovedFields(reportData, 'measured', cites.measured),
    adjusted_demand_kva: results.adjustedDemandKVA.toFixed(2),
    available_margin_kva: results.availableMarginKVA.toFixed(2),

    adequacy_result: results.adequacyResult,
    adequacy_status_text: adequacyStatusText,

    calculation_basis: buildCalculationBasis({
      provider: project.ami_data_provider || '',
      period: project.ami_measurement_period || '',
      intervalMinutes: project.ami_interval_minutes?.toString() || '',
      peakKW: results.maxRecordedDemandKW.toFixed(2),
      peakTimestamp,
      peakSeason: results.peakSeason,
      methodologyVersion,
      citation: cites.measured,
    }),

    equipment_list: equipmentList,

    assumptions_notes: project.assumptions_notes || '',

    gross_peak_kw: project.gross_peak_kw != null ? project.gross_peak_kw.toString() : null,
    ami_generation_column_detected: project.ami_generation_column_detected ?? false,
    demand_adjusted_for_solar: results.demandAdjustedForSolar,

    measured_citation: cites.measured,
    measured_article: cites.article,
    code_edition: cites.edition,
    measured_conductor_refs: cites.conductorSizing,
    edition_note: cites.editionNote,

    ...buildMethodsBlock(reportData),
  };
}

/**
 * Traditional (NEC 220.83 / 120.83) report data. Shares the identity,
 * preparer, service and adequacy field names with the measured formatter so
 * the template's common sections render identically; the trad_* fields drive
 * the worksheet sections. The measured formatter above is untouched.
 */
function formatTraditionalReportData(reportData: ReportData): ReportTemplateData {
  const { project, equipment } = reportData;
  const tr = reportData.traditionalResults;
  if (!tr) {
    throw new Error('formatTraditionalReportData requires traditionalResults');
  }
  const isDraft = reportData.isDraft ?? false;

  const customerCityStateZip = [
    project.customer_city,
    project.customer_state,
    project.customer_zip,
  ]
    .filter(Boolean)
    .join(', ');

  const reportDate = project.report_date || new Date().toLocaleDateString('en-US');

  const pack = getEditionPack(tr.codeEdition);
  const tradCites = citationsFor(project);
  const cite = tr.sectionCitation;
  const kva = (va: number) => (va / 1000).toFixed(2);

  const existing = existingLoads(equipment);
  const proposed = enteredProposedLoads(equipment);

  const lineItemById = new Map(tr.lineItems.map((li) => [li.id, li]));
  const equipmentList = proposed.map((eq) => {
    const li = lineItemById.get(eq.id);
    return {
      equipment_name: eq.equipment_name || 'N/A',
      equipment_type: eq.equipment_type || 'Other',
      source: eq.source || 'Nameplate',
      quantity: eq.quantity?.toString() || '1',
      voltage: eq.voltage?.toString() || '',
      amps: eq.amps?.toString() || 'N/A',
      kw: eq.kw?.toString() || 'N/A',
      kva: li ? kva(li.connectedVA) : '0.00',
      // Concurrency is a measured-method concept; the traditional template
      // prints the treatment column from the worksheet instead.
      is_concurrent: '',
      concurrency_reason: '',
    };
  });

  const worksheetRows = tr.lineItems.map((li) => ({
    label: li.label,
    origin:
      li.origin === 'proposed' ? 'New' : li.origin === 'existing' ? 'Existing' : 'Dwelling',
    connected_va: li.connectedVA.toFixed(0),
    treatment: li.treatment,
    demand_va: li.demandVA === null ? '—' : li.demandVA.toFixed(0),
  }));

  const summaryRows: ReportTemplateData['trad_summary_rows'] = [];
  if (tr.excludedNoncoincidentVA > 0) {
    summaryRows.push({
      label: 'Noncoincident load excluded (larger of heating or cooling used)',
      value: `${kva(tr.excludedNoncoincidentVA)} kVA`,
      kind: 'plain',
    });
  }
  if (tr.categories2026) {
    const c = tr.categories2026;
    summaryRows.push(
      { label: 'Existing load (dwelling profile + inventory)', value: `${kva(c.existingPoolVA)} kVA`, kind: 'plain' },
      { label: `First ${pack.optional83TierKVA} kVA of existing and new load at 100%`, value: `${kva(c.tierFromExistingVA + c.tierFromNewVA)} kVA`, kind: 'plain' },
      { label: 'Remainder of existing load at 40%', value: `${kva(c.existingRemainderDemandVA)} kVA`, kind: 'plain' },
    );
    if (c.newEvseVA > 0) {
      summaryRows.push({ label: 'New EVSE at 80%', value: `${kva(c.newEvseDemandVA)} kVA`, kind: 'plain' });
    }
    if (c.newCentralResistanceHeatVA > 0) {
      summaryRows.push({ label: 'New central electric resistance space heating at 80%', value: `${kva(c.newCentralResistanceHeatDemandVA)} kVA`, kind: 'plain' });
    }
    if (c.otherNewVA > 0) {
      summaryRows.push({ label: 'All other new loads at 50%', value: `${kva(c.otherNewDemandVA)} kVA`, kind: 'plain' });
    }
  } else {
    const sp = tr.statedParameters;
    const stated = sp ? ' (preparer-stated)' : '';
    const tierK = sp?.tierKVA ?? pack.optional83TierKVA;
    const tierP = sp?.tierPercent ?? 100;
    const remP = sp?.remainderPercent ?? 40;
    summaryRows.push(
      { label: 'General load pool (connected)', value: `${kva(tr.poolVA)} kVA`, kind: 'plain' },
      { label: `First ${tierK} kVA of pool at ${tierP}%${stated}`, value: `${kva(tr.tierDemandVA)} kVA`, kind: 'plain' },
      { label: `Remainder of pool at ${remP}%${stated}`, value: `${kva(tr.poolDemandVA - tr.tierDemandVA)} kVA`, kind: 'plain' },
    );
    if (tr.hvacBucketVA > 0) {
      summaryRows.push({ label: 'Air-conditioning / space heating at 100%', value: `${kva(tr.hvacBucketVA)} kVA`, kind: 'plain' });
    }
    if (tr.evseFullVA > 0) {
      summaryRows.push({
        label: `EVSE on a separate line at ${sp?.evsePercent ?? 100}%${sp?.evsePercent != null && sp.evsePercent !== 100 ? ' (preparer-stated)' : ''}`,
        value: `${kva(tr.evseFullDemandVA)} kVA`,
        kind: 'plain',
      });
    }
  }
  summaryRows.push(
    { label: `Calculated demand (NEC ${cite})`, value: `${tr.totalDemandKVA.toFixed(2)} kVA`, kind: 'highlight' },
    { label: `Service Capacity (${project.service_rating_amps ?? ''}A @ ${project.service_voltage_text ?? ''})`, value: `${tr.serviceCapacityKVA.toFixed(2)} kVA`, kind: 'total' },
    { label: 'Available Margin', value: `${tr.availableMarginKVA.toFixed(2)} kVA`, kind: 'total' },
    { label: 'Service Utilization', value: `${tr.utilizationPercent.toFixed(1)}%`, kind: 'plain' },
  );

  const defaultsCount = existing.filter((eq) => eq.value_source === 'default').length;
  const defaultsDisclosure =
    existing.length > 0 && defaultsCount > 0
      ? `${defaultsCount} of ${existing.length} inventory nameplate values are standard defaults; the remainder were entered from equipment nameplates.`
      : '';

  // Derived from the edition the result was computed under, never read back
  // from the stored string: a project stamped 'NEC 220.83 v1.0' before its
  // edition was known must not print an Article 220 number on an Article 120
  // report.
  const methodologyVersion = `NEC ${pack.sectionMap.existingDwelling ?? '220.83'} v1.0`;

  const adequacyStatusText =
    tr.adequacyResult === 'PASS'
      ? `The existing service has adequate capacity for the proposed new loads under NEC ${cite} (optional calculation for existing dwelling units).`
      : `The existing service does NOT have adequate capacity for the proposed new loads under NEC ${cite}. Service upgrade required.`;

  const submethodText =
    tr.submethodApplied === '220.83A'
      ? `${cite} — no additional air-conditioning or electric space-heating equipment is being installed`
      : tr.submethodApplied === '220.83B'
        ? `${cite} — additional air-conditioning or electric space-heating equipment is being installed`
        : `${cite} — single-table method (${tr.codeEdition} edition, Article 120)`;

  const calculationBasis = [
    `Basis: dwelling load inventory — ${project.dwelling_floor_area_sqft ?? 0} ft², ` +
      `${project.small_appliance_circuit_count ?? 2} small-appliance and ` +
      `${project.laundry_circuit_count ?? 1} laundry branch circuit(s), ` +
      `${existing.length} existing fixed load(s) at nameplate.`,
    `Method: NEC ${cite}, ${tr.codeEdition} edition — ${methodologyVersion}. Supporting detail follows.`,
  ].join(' ');

  return {
    is_draft: isDraft,

    customer_name: project.customer_name || '',
    customer_address: project.customer_address || '',
    customer_city_state_zip: customerCityStateZip,

    prepared_by: project.prepared_by || '',
    preparer_title: project.preparer_title || '',
    preparer_license: project.preparer_license || '',
    company_name: project.company_name || '',
    company_address: formatCompanyAddress(project),
    firm_logo_src: reportData.firmLogoSrc || '',

    ahj_jurisdiction: project.ahj_jurisdiction || '',
    permit_number: project.permit_number || '',
    report_date: reportDate,
    methodology_version: methodologyVersion,

    service_voltage_text: project.service_voltage_text || '',
    service_rating_amps: project.service_rating_amps?.toString() || '',

    // Measured-only fields — present to satisfy the shared shape; the
    // traditional template renders none of them.
    ami_data_provider: '',
    ami_measurement_period: '',
    ami_occupancy_status: '',
    ami_interval_minutes: '',
    ami_data_quality_notes: '',
    ami_site_notes: '',
    ami_merge_disclosure: '',
    demand_chart_svg: '',
    demand_chart_caption: '',
    max_recorded_demand_kw: '',
    max_recorded_demand_kva: '',
    peak_timestamp: '',
    peak_season: '',
    existing_demand_kva: '',
    existing_demand_125_kva: '',
    total_concurrent_new_load_kva: '',

    service_capacity_kva: tr.serviceCapacityKVA.toFixed(2),
    utilization_percent: tr.utilizationPercent.toFixed(1),
    total_all_new_load_kva: kva(
      tr.lineItems
        .filter((li) => li.origin === 'proposed')
        .reduce((sum, li) => sum + li.connectedVA, 0)
    ),
    adjusted_demand_kva: tr.totalDemandKVA.toFixed(2),
    available_margin_kva: tr.availableMarginKVA.toFixed(2),

    adequacy_result: tr.adequacyResult,
    adequacy_status_text: adequacyStatusText,

    calculation_basis: calculationBasis,

    equipment_list: equipmentList,
    ...buildRemovedFields(reportData, 'traditional', tr.sectionCitation),

    assumptions_notes: project.assumptions_notes || '',

    gross_peak_kw: null,
    ami_generation_column_detected: false,
    demand_adjusted_for_solar: false,

    // Present on both templates so the shared sections — and the methods
    // comparison, when it runs — render from one resolver.
    measured_citation: tradCites.measured,
    measured_article: tradCites.article,
    code_edition: tr.codeEdition,
    measured_conductor_refs: tradCites.conductorSizing,
    edition_note: tradCites.editionNote,

    method: 'traditional',
    trad_citation: cite,
    trad_edition: tr.codeEdition,
    trad_submethod_text: submethodText,
    trad_worksheet_rows: worksheetRows,
    trad_summary_rows: summaryRows,
    trad_floor_area: project.dwelling_floor_area_sqft?.toString() ?? '',
    trad_small_appliance_circuits: (project.small_appliance_circuit_count ?? 2).toString(),
    trad_laundry_circuits: (project.laundry_circuit_count ?? 1).toString(),
    trad_inventory_attested: project.inventory_attested ?? false,
    trad_qualification: qualificationStatement(project) ?? '',
    trad_defaults_disclosure: defaultsDisclosure,
    trad_positions: tr.positionsApplied,
    trad_warnings: tr.warnings,

    ...buildMethodsBlock(reportData),
  };
}

export function validateReportData(reportData: ReportData): {
  isValid: boolean;
  missingFields: string[];
} {
  const missingFields: string[] = [];
  const { project } = reportData;

  if (!project.service_rating_amps) missingFields.push('Service Rating');

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}
